import { type FC, useRef, useState } from 'react';

import MoreVertical from '@crehana/compass.icons.outline/MoreVertical';
import { Popper, useUITheme } from '@crehana/compass.ui';
import { cn } from '@crehana/compass.ui/v2';
import { useClickAway } from '@crehana/react-hooks';

import type { ActionsPopperProps } from './types';

const ActionsPopper: FC<React.PropsWithChildren<ActionsPopperProps>> = ({
  actions,
}) => {
  const popperRef = useRef(null);
  const anchorRef = useRef(null);
  const { isDark } = useUITheme();
  const [showActions, setShowActions] = useState(false);

  useClickAway(popperRef, () => setShowActions(false));

  return (
    <div className="tw-flex">
      <div ref={anchorRef} className="relative">
        <button
          type="button"
          className={cn(
            'tw-z-2 tw-p-0.8 tw-rounded-full tw-flex tw-justify-center tw-items-center tw-transition-all',
            {
              'hover:tw-bg-neutral-light-200': !showActions && !isDark,
              'hover:tw-bg-elevation-dark-02': showActions && isDark,
              'tw-bg-neutral-light-400': showActions && !isDark,
              'tw-bg-elevation-dark-01': showActions && isDark,
            },
          )}
          onClick={() => setShowActions(!showActions)}
        >
          <MoreVertical
            size={24}
            className={
              isDark ? 'tw-text-neutral-dark-500' : 'tw-text-neutral-light-700'
            }
          />
        </button>
      </div>
      <Popper
        isVisible={showActions}
        anchorRef={anchorRef}
        ref={popperRef}
        position="left"
      >
        <div className="dark:tw-bg-neutral-dark-700 tw-bg-neutral-dark-100 tw-rounded-0.8 tw-absolute tw-w-20 tw-z-7 tw-right-0 tw-overflow-hidden shadow-tooltip">
          {actions
            .filter(a => !a.disable)
            .map(action => {
              const { handler, label, isDanger, icon } = action;

              const darkStyle = `hover:bg-base-light-dark-mode ${
                isDanger ? 'tw-alert-400' : ''
              }`;
              const lightStyle = `hover:bg-gray-lighter ${
                isDanger ? 'tw-text-alert-400' : 'hover:text-primary-main'
              }`;

              return (
                <div key={`action-popper-${label}`}>
                  <button
                    type="button"
                    onClick={handler}
                    className={`tw-w-full font-body4 tw-px-1.6 tw-py-1.2 text-left tw-flex tw-items-center ${
                      isDark ? darkStyle : lightStyle
                    }`}
                  >
                    {icon && icon}
                    <span className={icon ? 'tw-ml-0.8' : ''}>{label}</span>
                  </button>
                </div>
              );
            })}
        </div>
      </Popper>
    </div>
  );
};

export default ActionsPopper;
